// Product configurator for sections/sm-main-product_navigation.liquid, moved out of the section to keep
// its Liquid under Shopify's 256 KB limit. Each section renders a <script type="application/json"
// data-sm-nav-config> with its id and the Liquid values the configurator needs (option names, currency,
// labels, initial variant, money format). Runs once per section; re-runs for sections the theme
// editor reloads.
(() => {
  const initConfigurator = (config) => {
    const sectionId = config.sectionId;
    const root = document.getElementById(`MainProduct-${sectionId}`);
    if (!root || root.dataset.smConfiguratorReady === 'true') return;
    root.dataset.smConfiguratorReady = 'true';

    const dataNode = root.querySelector('[data-sm-product-json]');
    if (!dataNode) return;

    const productData = JSON.parse(dataNode.textContent);
    const purchasableNode = root.querySelector('[data-sm-variant-purchasable]');
    const purchasableVariants = purchasableNode ? JSON.parse(purchasableNode.textContent) : {};
    const optionNames = config.optionNames || [];
    const currency = config.currency;
    const addButtonLabel = config.addToCartLabel;
    const soldOutLabel = config.soldOutLabel;
    const initialVariantId = config.initialVariantId;
    /* The real form lives in <product-form>. The price block's Shop Pay
       installment forms also post to /cart/add and come first in the DOM,
       so an unscoped query submitted those instead (full-page jump to /cart). */
    const productForm =
      root.querySelector('product-form form[action*="/cart/add"]') ||
      root.querySelector('form[action*="/cart/add"]:not(.installment)');

    let currentVariant =
      productData.variants.find((variant) => Number(variant.id) === Number(initialVariantId)) ||
      productData.variants[0];
    let selectedOptions = currentVariant ? currentVariant.options.slice() : [];

    /* State that has to outlive Dawn re-rendering the info column. */
    const chosenUpsells = new Map();   // upsell product id -> chosen variant id
    const expandedCards = new Set();   // upsell product ids currently open
    let applying = false;              // true while we are writing to the DOM

    const money = (cents) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, config.moneyFormat);
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', {
        style: 'currency', currency: currency
      }).format((Number(cents) || 0) / 100);
    };

    const findVariant = () => productData.variants.find((variant) =>
      variant.options.every((value, index) => value === selectedOptions[index])
    );

    /* The base configurator must remain purchasable independently of a
       sold-out linked product (unavailable upsells are disabled separately
       in Liquid), but the base variant itself has to be in stock: Shopify
       accepted sold-out variants that this re-enabled the button for. */
    /* Camouflage (market variant hiding). The app only runs where it finds a
       native dropdown, so templates using the custom Size / Power controls
       keep a hidden variants block for it; its result (hoos:executed) is
       mirrored onto those controls here. */
    let camoHidden = new Set();
    const isCamoHidden = (variant) => Boolean(variant) && camoHidden.has(String(variant.id));
    const valueCamoHidden = (position, value) => {
      const matching = productData.variants.filter((variant) => variant.options[position - 1] === value);
      return matching.length > 0 && matching.every(isCamoHidden);
    };

    const variantCanPurchase = (variant) => {
      if (!variant || !variant.id) return false;
      if (isCamoHidden(variant)) return false;
      const purchasable = purchasableVariants[String(variant.id)];
      return typeof purchasable === 'boolean' ? purchasable : variant.available !== false;
    };

    const cardKey = (card) => card && (card.dataset.smUpsellProductId || '');

    const syncNativePicker = (position, value) => {
      const picker = root.querySelector('variant-selects, product-variant-picker');
      if (!picker) return false;
      const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
      const fieldset = fieldsets[position - 1];
      if (fieldset) {
        const nativeInput = Array.from(fieldset.querySelectorAll('input')).find((input) => input.value === value);
        if (nativeInput && !nativeInput.checked) {
          nativeInput.click();
          return true;
        }
      }
      const selects = Array.from(picker.querySelectorAll('select'));
      const select = selects[position - 1];
      if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
      return false;
    };

    const updateMedia = (variant) => {
      const mediaId = variant && (variant.featured_media && variant.featured_media.id || variant.featured_image && variant.featured_image.id);
      if (!mediaId) return;
      const gallery = root.querySelector('media-gallery');
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        /* Variant images are left out of the gallery list (product-media-gallery-new);
           the main thumb shows them. Only move the list when the slide exists, or
           setActiveMedia throws and stops the Camouflage reveal. */
        const slideId = `${root.dataset.section}-${mediaId}`;
        if (gallery.querySelector(`ul [data-media-id="${slideId}"]`)) gallery.setActiveMedia(slideId, true);
        return;
      }
      const thumbnail = root.querySelector(`[data-media-id$="-${mediaId}"], [data-target$="-${mediaId}"]`);
      if (thumbnail) thumbnail.click();
    };

    const selectedUpsellCheckboxes = () => Array.from(
      root.querySelectorAll('[data-sm-upsell-card]:not([hidden]) [data-sm-upsell-checkbox]:checked:not(:disabled)')
    );

    const selectedUpsellTotal = () => selectedUpsellCheckboxes()
      .reduce((total, checkbox) => total + Number(checkbox.dataset.smUpsellPrice || 0), 0);

    const humanList = (values) => {
      if (values.length < 2) return values[0] || '';
      if (values.length === 2) return `${values[0]} and ${values[1]}`;
      return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
    };

    const updateOrderSummary = (variant, totalPrice, totalComparePrice, onSale) => {
      const summary = root.querySelector('[data-sm-order-summary]');
      if (!summary) return;

      const selectedPrefix = summary.dataset.selectedPrefix || "You've selected";
      const upsellPrefix = summary.dataset.upsellPrefix || "You've also selected";
      const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      const productTitle = productData.title || '';
      const selectedUpsells = selectedUpsellCheckboxes()
        .map((checkbox) => checkbox.closest('[data-sm-upsell-card]')?.dataset.smUpsellTitle)
        .filter(Boolean);
      const uniqueUpsells = Array.from(new Set(selectedUpsells));

      let summaryText = `${selectedPrefix} ${variantTitle ? `${variantTitle} — ` : ''}${productTitle}.`;
      if (uniqueUpsells.length) summaryText += ` ${upsellPrefix} ${humanList(uniqueUpsells)}.`;

      const textNode = summary.querySelector('[data-sm-order-text]');
      const totalNode = summary.querySelector('[data-sm-order-total]');
      const compareNode = summary.querySelector('[data-sm-order-compare]');
      if (textNode) textNode.textContent = summaryText;
      if (totalNode) totalNode.innerHTML = money(totalPrice);
      if (compareNode) {
        compareNode.hidden = !onSale;
        compareNode.innerHTML = onSale ? `<del>Was</del> ${money(totalComparePrice)}` : '';
      }
    };

    const updateDisplayedPrice = (variant) => {
      if (!variant) return;
      const extrasTotal = selectedUpsellTotal();
      const totalPrice = Number(variant.price) + extrasTotal;
      const variantComparePrice = Number(variant.compare_at_price || 0);
      const totalComparePrice = (variantComparePrice > Number(variant.price) ? variantComparePrice : Number(variant.price)) + extrasTotal;
      const onSale = variantComparePrice > Number(variant.price);

      /* innerHTML: the shop's money format wraps the amount in a .money span,
         which textContent printed as literal markup. */
      /* A price sourced from linked products (data-sm-price-locked) is not this product's own. */
      root.querySelectorAll(`[id^="price-${sectionId}"]:not([data-sm-price-locked]) .price`).forEach((priceRoot) => {
        priceRoot.classList.toggle('price--on-sale', onSale);
        priceRoot.querySelectorAll('.price-item--regular').forEach((node) => {
          node.innerHTML = money(onSale ? totalComparePrice : totalPrice);
        });
        priceRoot.querySelectorAll('.price-item--sale').forEach((node) => {
          node.innerHTML = money(totalPrice);
        });
      });
      updateOrderSummary(variant, totalPrice, totalComparePrice, onSale);
      window.smPublishVariantTokens(root, productData, variant, selectedUpsellCheckboxes());
    };

    const updateFallbackProductUI = (variant) => {
      if (!variant) return;
      root.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
        input.value = variant.id;
      });
      updateDisplayedPrice(variant);
      const canPurchase = variantCanPurchase(variant);
      const purchaseButtons = Array.from(root.querySelectorAll(
        'form[action*="/cart/add"] [name="add"], form[action*="/cart/add"] .product-form__submit, .product-form__actions-wrapper button'
      )).filter((button) =>
        button.name === 'add' ||
        button.classList.contains('product-form__submit') ||
        /* Not "notify me" / "back in stock": those belong to stock-alert apps
           and must keep their own behaviour. */
        /add to cart|sold out/i.test(button.textContent || '')
      );

      purchaseButtons.forEach((addButton) => {
        addButton.disabled = !canPurchase;
        addButton.setAttribute('aria-disabled', canPurchase ? 'false' : 'true');
        addButton.classList.toggle('disabled', !canPurchase);
        addButton.dataset.smPrimarySubmit = 'true';
        if (canPurchase) {
          addButton.setAttribute('type', 'submit');
          addButton.setAttribute('name', 'add');
          if (productForm && productForm.id && !addButton.closest('form')) {
            addButton.setAttribute('form', productForm.id);
          }
        }
        const label = addButton.querySelector('[data-sm-add-label], .product-form__submit-text, :scope > span:not(.loading__spinner)') || addButton.querySelector('span');
        if (label) {
          label.textContent = canPurchase ? addButtonLabel : soldOutLabel;
        } else {
          addButton.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
              node.textContent = canPurchase ? addButtonLabel : soldOutLabel;
            }
          });
        }
      });
      updateMedia(variant);
    };

    const updateOptionControls = () => {
      root.querySelectorAll('[data-sm-smoker-size], [data-sm-power-source]').forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        const selected = selectedOptions[position - 1];
        group.querySelectorAll('[data-sm-option-value]').forEach((control) => {
          const camoHide = valueCamoHidden(position, control.dataset.smOptionValue);
          const card = control.closest('.smoker_size') || control;
          card.hidden = camoHide;
          card.style.display = camoHide ? 'none' : '';
          const active = control.dataset.smOptionValue === selected;
          control.classList.toggle('active', active);
          control.setAttribute('aria-pressed', active ? 'true' : 'false');
          if (control.matches('input')) control.checked = active;
        });
      });
    };

    /* Hide-only pass for Camouflage-hidden values, run straight from the MutationObserver when
       Dawn re-renders the info column (server HTML knows nothing about Camouflage). Observer
       callbacks run before the next paint, so hidden values never flash. Writes only `hidden`
       and inline display, which the observer doesn't watch. */
    const hideCamouflagedControls = () => {
      if (!camoHidden.size) return;
      root.querySelectorAll('[data-sm-smoker-size], [data-sm-power-source]').forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        group.querySelectorAll('[data-sm-option-value]').forEach((control) => {
          if (!valueCamoHidden(position, control.dataset.smOptionValue)) return;
          const card = control.closest('.smoker_size') || control;
          card.hidden = true;
          card.style.display = 'none';
        });
        if (group.matches('[data-sm-power-source]') && valueCamoHidden(position, group.dataset.wirelessValue)) {
          const panel = root.querySelector('[data-sm-wireless-panel]');
          if (panel) {
            panel.hidden = true;
            panel.style.display = 'none';
          }
        }
      });
    };

    const firstVisibleWiredValue = (group, position) =>
      Array.from(group.querySelectorAll('[data-sm-option-value]'))
        .map((control) => control.dataset.smOptionValue)
        .find((value) => value !== group.dataset.wirelessValue && !valueCamoHidden(position, value));

    const setCollapsibleBody = (body, expanded) => {
      if (!body) return;
      body.classList.toggle('is-active', expanded);
      body.classList.toggle('show', expanded);
      body.style.opacity = expanded ? '1' : '0';
      body.style.height = expanded ? 'auto' : '0px';
    };

    const updateWirelessPanel = () => {
      const group = root.querySelector('[data-sm-power-source]');
      const panel = root.querySelector('[data-sm-wireless-panel]');
      if (!group || !panel) return;
      const position = Number(group.dataset.optionPosition);
      const wirelessValue = group.dataset.wirelessValue;
      const wirelessSelected = selectedOptions[position - 1] === wirelessValue;

      /* Open exactly when cordless is selected: the card and the cordless tab are one choice. */
      const expanded = wirelessSelected;

      const wirelessHidden = valueCamoHidden(position, wirelessValue);
      panel.hidden = wirelessHidden;
      panel.style.display = wirelessHidden ? 'none' : '';

      const header = panel.querySelector('[data-sm-wireless-toggle]');
      const checkbox = header && header.querySelector('input[type="checkbox"]');
      const body = panel.querySelector('.power_source-body');

      if (header) {
        /* is-active drives the tick and the card border, so it tracks the
           SELECTION, not whether the body happens to be open. */
        header.classList.toggle('is-active', wirelessSelected);
        header.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
      if (checkbox) checkbox.checked = wirelessSelected;
      setCollapsibleBody(body, expanded);

      const wirelessOptions = selectedOptions.slice();
      wirelessOptions[position - 1] = wirelessValue;
      const wirelessVariant = productData.variants.find((variant) => variant.options.every((value, index) => value === wirelessOptions[index]));
      /* Compare against the wired option actually selected, or (with wireless selected) the wired
         option this market can see, never a Camouflage-hidden one. Prices are the market's own. */
      const currentPower = selectedOptions[position - 1];
      const wiredValue = currentPower !== wirelessValue ? currentPower : firstVisibleWiredValue(group, position);
      const wiredOptions = selectedOptions.slice();
      wiredOptions[position - 1] = wiredValue;
      const wiredVariant = wiredValue && productData.variants.find((variant) => variant.options.every((value, index) => value === wiredOptions[index]));
      const difference = wirelessVariant && wiredVariant ? wirelessVariant.price - wiredVariant.price : 0;
      const differenceNode = panel.querySelector('[data-sm-wireless-difference]');
      if (differenceNode) differenceNode.innerHTML = difference > 0 ? `+ ${money(difference)}` : '';
      /* Comparison: the cordless variant's compare-at price against the same wired price, struck through. */
      const wasNode = panel.querySelector('[data-sm-wireless-was]');
      if (wasNode) {
        const wasDifference = wirelessVariant && wiredVariant && wirelessVariant.compare_at_price > wirelessVariant.price
          ? wirelessVariant.compare_at_price - wiredVariant.price
          : 0;
        const showWas = wasDifference > difference;
        wasNode.hidden = !showWas;
        wasNode.innerHTML = showWas ? `<s>+ ${money(wasDifference)}</s>` : '';
      }
    };

    const setUpsellExpanded = (card, expanded, remember = true) => {
      if (!card) return;
      const header = card.querySelector('[data-sm-upsell-toggle]');
      const body = card.querySelector('.power_source-body');
      if (header) header.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      setCollapsibleBody(body, expanded);
      if (!remember) return;
      const key = cardKey(card);
      if (!key) return;
      if (expanded) expandedCards.add(key); else expandedCards.delete(key);
    };

    const updateUpsells = () => {
      const seen = new Set();
      root.querySelectorAll('[data-sm-upsell-card]').forEach((card) => {
        const ownerId = card.dataset.smOwnerVariantId;
        let visible = !ownerId || Number(ownerId) === Number(currentVariant.id);
        const productId = card.dataset.smUpsellProductId;
        if (visible && seen.has(productId)) visible = false;
        if (visible) seen.add(productId);
        card.hidden = !visible;

        const checkbox = card.querySelector('[data-sm-upsell-checkbox]');

        if (!visible) {
          if (checkbox) checkbox.checked = false;
          setUpsellExpanded(card, false, false);
          return;
        }

        /* Re-apply what the buyer picked before Dawn wiped the column. */
        const savedVariantId = chosenUpsells.get(productId);
        if (checkbox && !checkbox.disabled && savedVariantId) {
          checkbox.value = savedVariantId;
          checkbox.checked = true;
          const select = card.querySelector('[data-sm-upsell-variant]');
          if (select && select.value !== savedVariantId) select.value = savedVariantId;
        } else if (checkbox) {
          checkbox.checked = false;
        }
        setUpsellExpanded(card, expandedCards.has(productId), false);
      });

      const wrapper = root.querySelector('[data-sm-complete-rig]');
      if (wrapper) wrapper.hidden = !wrapper.querySelector('[data-sm-upsell-card]:not([hidden])');
    };

    const updateAll = () => {
      const next = findVariant();
      if (!next) return;                 // never clobber a good variant with undefined
      currentVariant = next;
      applying = true;
      try {
        updateOptionControls();
        updateWirelessPanel();
        updateUpsells();
        updateFallbackProductUI(currentVariant);
      } finally {
        setTimeout(() => { applying = false; }, 0);
      }
      restoreScroll();
    };

    const chooseOption = (position, value) => {
      /* Already on this value — ignore. Stops a label's synthesized click
         from firing a second variant fetch. */
      if (selectedOptions[position - 1] === value && findVariant()) return;

      const previous = selectedOptions.slice();
      selectedOptions[position - 1] = value;

      let candidate = findVariant();
      if (!candidate) {
        /* Combination doesn't exist. Keep the option the buyer just clicked
           and snap the rest to a variant that does, preferring in-stock. */
        candidate =
          productData.variants.find((v) => v.options[position - 1] === value && v.available && !isCamoHidden(v)) ||
          productData.variants.find((v) => v.options[position - 1] === value && !isCamoHidden(v)) ||
          productData.variants.find((v) => v.options[position - 1] === value);
        if (!candidate) { selectedOptions = previous; return; }
        selectedOptions = candidate.options.slice();
      }

      currentVariant = candidate;
      syncNativePicker(position, selectedOptions[position - 1]);
      updateAll();
    };

    /* Scroll hold. The info column is sticky; once its bottom is pinned to the end of the (taller)
       gallery, anything that grows it (an upsell or the wireless card opening, a re-render after a
       variant change) pushes everything above it up the screen. Keep whatever the shopper clicked in
       the same place: note its position, then cancel any shift with an instant scroll. Runs from the
       handlers, updateAll and the MutationObserver (before paint); stops as soon as they scroll. */
    let scrollAnchor = null;
    const anchorSelectorFor = (target) => {
      const upsell = target.closest('[data-sm-upsell-card]');
      if (upsell && upsell.dataset.smUpsellProductId) {
        return `[data-sm-upsell-card][data-sm-upsell-product-id="${CSS.escape(upsell.dataset.smUpsellProductId)}"]:not([hidden])`;
      }
      if (target.closest('[data-sm-wireless-panel]')) return '[data-sm-wireless-panel]';
      const accordion = target.closest('.acc-toggle[data-target]');
      if (accordion) return `.acc-toggle[data-target="${CSS.escape(accordion.dataset.target)}"]`;
      const control = target.closest('[data-sm-option-value]') || target.closest('.smoker_size')?.querySelector('[data-sm-option-value]');
      if (control) return `[data-sm-option-value="${CSS.escape(control.dataset.smOptionValue)}"]`;
      return null;
    };
    const anchorElement = (selector) => {
      const found = root.querySelector(selector);
      return found ? found.closest('.smoker_size') || found : null;
    };
    const holdScroll = (target) => {
      const selector = anchorSelectorFor(target);
      const el = selector && anchorElement(selector);
      if (!el) return;
      scrollAnchor = { selector, top: el.getBoundingClientRect().top, until: performance.now() + 3000 };
    };
    const restoreScroll = () => {
      if (!scrollAnchor) return;
      if (performance.now() > scrollAnchor.until) {
        scrollAnchor = null;
        return;
      }
      const el = anchorElement(scrollAnchor.selector);
      if (!el) return;
      const shift = el.getBoundingClientRect().top - scrollAnchor.top;
      if (Math.abs(shift) > 1) window.scrollBy({ top: shift, behavior: 'instant' });
    };
    const releaseScroll = () => {
      scrollAnchor = null;
    };
    window.addEventListener('wheel', releaseScroll, { passive: true });
    window.addEventListener('touchmove', releaseScroll, { passive: true });
    window.addEventListener('keydown', (event) => {
      if (['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown'].includes(event.key) || (event.key === ' ' && event.target === document.body)) releaseScroll();
    });
    const scrollHoldTargets = '[data-sm-upsell-toggle], [data-sm-wireless-toggle], [data-sm-option-value], .smoker_size, [data-sm-upsell-checkbox], [data-sm-wireless-checkbox]';
    /* Accordions (FAQs, description) open with a height transition, so the column keeps growing for
       several frames after the click: hold from the capture phase (before the accordion script runs)
       and re-apply on every resize of the column while the hold lasts. */
    root.addEventListener('click', (event) => {
      if (event.target.closest('.product__info-container .acc-toggle[data-target]')) holdScroll(event.target);
    }, true);
    if ('ResizeObserver' in window) {
      const holdColumn = root.querySelector('.product__info-container');
      if (holdColumn) new ResizeObserver(() => restoreScroll()).observe(holdColumn);
    }

    root.addEventListener('click', (event) => {
      if (event.target.closest(scrollHoldTargets)) {
        holdScroll(event.target);
        queueMicrotask(restoreScroll);
      }
      /* Stock-notification apps sometimes replace the real submit control
         with a type=button element. Route that visible control back through
         the product form so the main item and checked upsells share the
         same atomic cart request. */
      const purchaseTrigger = event.target.closest('[data-sm-primary-submit]');
      if (purchaseTrigger && productForm) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (typeof productForm.requestSubmit === 'function') {
          productForm.requestSubmit();
        } else {
          productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
        return;
      }

      /* Clicking anywhere on an upsell heading selects/deselects that item
         and opens/closes its details. A direct checkbox click is left to
         the browser so it fires exactly one change event. */
      const toggle = event.target.closest('[data-sm-upsell-toggle], [data-sm-wireless-toggle]');
      if (toggle && !event.target.closest('input, select, label, a, button')) {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        if (toggle.matches('[data-sm-wireless-toggle]')) {
          /* The card is the cordless variant: clicking it selects cordless, or (if selected) goes back
             to the first visible wired option. The change handler does the selecting. */
          const checkbox = toggle.querySelector('[data-sm-wireless-checkbox]');
          if (checkbox) {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          const card = toggle.closest('[data-sm-upsell-card]');
          const checkbox = card && card.querySelector('[data-sm-upsell-checkbox]');
          if (checkbox && !checkbox.disabled) {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            setUpsellExpanded(card, !expanded);
          }
        }
        return;
      }

      let optionControl = event.target.closest('[data-sm-option-value]');
      if (!optionControl) {
        /* data-sm-option-value lives on the hidden radio, which is a sibling
           of the card's text — so clicking the text has to be resolved here. */
        const sizeCard = event.target.closest('.smoker_size');
        if (sizeCard) optionControl = sizeCard.querySelector('[data-sm-option-value]');
      }
      if (optionControl) {
        event.preventDefault();
        const group = optionControl.closest('[data-option-position]');
        if (!group) return;
        chooseOption(Number(group.dataset.optionPosition), optionControl.dataset.smOptionValue);
      }
    }, true);

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-sm-upsell-checkbox], [data-sm-wireless-checkbox]')) {
        if (!scrollAnchor) holdScroll(event.target);
        queueMicrotask(restoreScroll);
      }
      if (event.target.matches('[data-sm-wireless-checkbox]')) {
        const group = root.querySelector('[data-sm-power-source]');
        if (!group) return;
        const position = Number(group.dataset.optionPosition);
        const visibleWired = firstVisibleWiredValue(group, position);
        const nextValue = event.target.checked ? group.dataset.wirelessValue : visibleWired || group.dataset.wiredValue;
        if (nextValue) chooseOption(position, nextValue);
        return;
      }

      if (event.target.matches('[data-sm-upsell-checkbox]')) {
        const card = event.target.closest('[data-sm-upsell-card]');
        const key = cardKey(card);
        if (key) {
          if (event.target.checked) chosenUpsells.set(key, event.target.value);
          else chosenUpsells.delete(key);
        }
        setUpsellExpanded(card, event.target.checked);
        updateDisplayedPrice(currentVariant);
        return;
      }

      if (event.target.matches('[data-sm-upsell-variant]')) {
        const card = event.target.closest('[data-sm-upsell-card]');
        const checkbox = card && card.querySelector('[data-sm-upsell-checkbox]');
        const selectedOption = event.target.selectedOptions[0];
        if (checkbox && selectedOption) {
          checkbox.value = event.target.value;
          checkbox.dataset.smUpsellPrice = selectedOption.dataset.price || '0';
          const priceLabel = card.querySelector('[data-sm-upsell-price-label]');
          if (priceLabel) priceLabel.innerHTML = `+ ${money(selectedOption.dataset.price || 0)}`;
          const key = cardKey(card);
          if (key && checkbox.checked) chosenUpsells.set(key, checkbox.value);
          updateDisplayedPrice(currentVariant);
        }
        return;
      }

      const picker = event.target.closest('variant-selects, product-variant-picker');
      if (!picker) return;
      const values = [];
      picker.querySelectorAll('fieldset').forEach((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        if (checked) values.push(checked.value);
      });
      picker.querySelectorAll('select').forEach((select) => values.push(select.value));
      if (values.length === optionNames.length) {
        selectedOptions = values;
        updateAll();
      }
    });

    root.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      if (!currentVariant) return;   // let Shopify's own hidden id handle it
      const extras = selectedUpsellCheckboxes();
      if (!extras.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const submitButton = form.querySelector('[name="add"]');
      if (submitButton) submitButton.disabled = true;

      if (!variantCanPurchase(currentVariant)) {
        window.alert('This product combination is currently unavailable. Please select another option.');
        if (submitButton) submitButton.disabled = false;
        return;
      }

      const formData = new FormData(form);
      const mainItem = {
        id: Number(currentVariant.id),
        quantity: Number(formData.get('quantity') || 1)
      };
      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) mainItem.properties = properties;
      if (formData.get('selling_plan')) mainItem.selling_plan = Number(formData.get('selling_plan'));

      const queuedItems = [
        { item: mainItem, label: `${productData.title || 'Main product'} — ${currentVariant.title || ''}` },
        ...extras.map((checkbox) => ({
          item: { id: Number(checkbox.value), quantity: 1 },
          label: checkbox.closest('[data-sm-upsell-card]')?.dataset.smUpsellTitle || 'Selected upsell'
        }))
      ];
      /* Shopify adds each line to the top of the cart: post upsells first (reversed) and the
         product last, so the cart reads product, then upsells in their listed order. */
      const items = queuedItems.map((entry) => entry.item).reverse();
      const invalidItem = items.find((item) => !Number.isSafeInteger(item.id) || item.id <= 0 || !Number.isFinite(item.quantity) || item.quantity < 1);
      if (invalidItem) {
        window.alert('One of the selected products has an invalid variant. Please refresh the page and try again.');
        if (submitButton) submitButton.disabled = false;
        return;
      }

      const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items })
        });
        const responseText = await response.text();
        let result = {};
        try {
          result = responseText ? JSON.parse(responseText) : {};
        } catch (parseError) {
          result = { description: responseText };
        }
        if (!response.ok) {
          const responseErrors = result.errors
            ? (typeof result.errors === 'string' ? result.errors : JSON.stringify(result.errors))
            : '';
          const reason = result.description || result.message || responseErrors || `Shopify returned error ${response.status}`;
          throw new Error(`Unable to add the selected products: ${reason}.`);
        }

        /* The theme drawer expects the single-item product-form response
           and throws when it receives our multi-item payload. Going to the
           cart is reliable and shows the base product plus every upsell. */
        window.location.href = cartUrl;
      } catch (error) {
        console.error('[SM product configurator]', error);
        window.alert(error.message);
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    }, true);

    const revealCamouflaged = () => {
      if (!root.classList.contains('sm-camo-pending')) return;
      /* Cascade: each visible block in the info column 70ms after the one above (capped). */
      const container = root.querySelector('.product__info-container');
      const blocks = container
        ? Array.from(container.children).filter((el) => !el.matches('style, script') && el.getBoundingClientRect().height > 0)
        : [];
      blocks.forEach((el, index) => el.style.setProperty('--sm-cascade-delay', `${Math.min(index, 8) * 70}ms`));
      root.classList.remove('sm-camo-pending');
      setTimeout(() => blocks.forEach((el) => el.style.removeProperty('--sm-cascade-delay')), 1500);
      try {
        performance.mark('sm-camo-ready');
      } catch (error) {
        /* Timing mark is QA-only. */
      }
    };

    const applyCamouflage = (detail) => {
      if (!detail || !detail.product || String(detail.product.id) !== String(productData.id)) return;
      camoHidden = new Set((detail.hiddenVariants || []).map((variant) => String(variant.id)));
      if (isCamoHidden(currentVariant)) {
        const next =
          productData.variants.find((v) => v.available && !isCamoHidden(v)) ||
          productData.variants.find((v) => !isCamoHidden(v));
        if (next) {
          selectedOptions = next.options.slice();
          currentVariant = next;
          selectedOptions.forEach((value, index) => syncNativePicker(index + 1, value));
        }
      }
      /* Reveal even if a UI update throws, so the info column never waits for the 4s CSS fallback. */
      try {
        updateAll();
      } finally {
        revealCamouflaged();
      }
    };
    (window.smHoosResults || []).forEach(applyCamouflage);
    document.addEventListener('hoos:executed', (event) => applyCamouflage(event.detail));

    /* Dawn's product-info.js swaps the innerHTML of the info column on every
       variant change, which throws away ticked upsells and open panels.
       Re-apply once the swap settles. The `applying` flag keeps our own
       writes from retriggering this. */
    const infoColumn = root.querySelector(`#ProductInfo-${sectionId}`);
    if (infoColumn) {
      let pending;
      new MutationObserver((mutations) => {
        if (applying) return;
        if (mutations.some((mutation) => mutation.type === 'childList')) {
          hideCamouflagedControls();
          restoreScroll();
        }
        clearTimeout(pending);
        pending = setTimeout(updateAll, 50);
      }).observe(infoColumn, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class']
      });
    }

    updateAll();
    /* Stock/notify apps commonly initialise after the product section.
       Re-assert the selected variant's real Shopify availability once
       those initialisers have finished, without changing the design. */
    setTimeout(() => updateFallbackProductUI(currentVariant), 250);
    setTimeout(() => updateFallbackProductUI(currentVariant), 1000);
  
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('script[data-sm-nav-config]').forEach((node) => {
      try {
        initConfigurator(JSON.parse(node.textContent));
      } catch (error) {
        console.error('[SM product configurator]', error);
      }
    });
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
